// src/projectsConfig.ts

export type ProjectDef = {
  id: string;
  name: string;
  iconSrc: string;    // small square PNG for the left grid
  headerSrc: string;  // wide header image at top of right column
  platform: string;
  description: string;
  ctaLabel: string;
  href?: string;      // optional redirect URL

  /**
   * Optional password gate:
   * - If true, clicking Play will show the Password window.
   * - The input is hashed with SHA-256 and compared against codeHash.
   *
   * IMPORTANT: For better security, do NOT store the raw code in the frontend.
   * Generate the SHA-256 hash offline and paste the hex string into codeHash.
   */
  requiresCode?: boolean;
  codeHash?: string; // SHA-256 hash of the correct code (hex string, lowercase)

  /**
   * Optional video playback.
   * When defined, a play icon overlay appears on the header image.
   * Clicking it opens the video player window, which starts automatically.
   *
   * Accepted formats:
   *   Local file    → "/videos/project-demo.mp4"
   *   YouTube       → "https://www.youtube.com/watch?v=VIDEO_ID"
   *                   "https://youtu.be/VIDEO_ID"
   *   Google Drive  → "https://drive.google.com/file/d/FILE_ID/view"
   *                   File must be shared as "Anyone with the link can view".
   *                   Custom progress bar is not available for Drive videos
   *                   (Google Drive has no playback API).
   */
  videoSrc?: string;

  /**
   * YouTube only — playback start position in seconds.
   * The video will begin from this timecode instead of the beginning.
   * Has no effect on local files or Google Drive videos.
   *
   * Example: videoStartTime: 90  → starts at 1:30
   */
  videoStartTime?: number;
};

export type ProjectFolder = {
  id: string;
  name: string;
  iconSrc: string;    // folder icon
  /** Items within this folder — projects, or further folders. */
  projects: ProjectItem[];
};

export type ProjectItem = ProjectDef | ProjectFolder;

export function isFolder(item: ProjectItem): item is ProjectFolder {
  return 'projects' in item;
}

export const PROJECTS: ProjectItem[] = [
  {
    id: "blm",
    name: "Black Lotus Motel",
    iconSrc: "/images/projects/Icon-BLM.png",
    headerSrc: "/images/projects/Header-BLM.png",
    platform: "Co-op · Steam",
    description:
      "Cooperative online escape game in a location filled with puzzles and secrets.",
    ctaLabel: "Play",
    href: "https://store.steampowered.com/app/1677400/Black_Lotus_Motel/",
    // requiresCode: true,
    // codeHash: "<paste sha256 hex here>",
    // Example video - testing video player feature (replace with actual video):
    videoSrc: "https://www.youtube.com/watch?v=gjaRvd59bdg",
    videoStartTime: 142,
  },
  {
    // Display name only - the id, route and Unity build name stay "shooter"/"Shooter"
    // so the R2 build URLs keep resolving.
    id: "shooter",
    name: "OÍD MORTALES",
    iconSrc: "/images/projects/Icon-Shooter.png",
    headerSrc: "/images/projects/Header-Shooter.png",
    platform: "TBA",
    description:
      "Fast-paced top-down shooter prototype. Currently in development.",
    ctaLabel: "Coming Soon",
    href: "/projects/shooter",
    // Example of a gated project (uncomment & replace with real hash if needed):
     requiresCode: true,
     codeHash: "17fb2ccc22984bcddbaf825e4270eca1e8fe1b66e019af3bfdc331e71787bccc",
  },
  {
    id: "folder-web3",
    name: "Web3",
    iconSrc: "/images/Icon-Projects.svg",
    projects: [
      {
        id: "jumper",
        name: "Let's Flamingo!",
        iconSrc: "/images/projects/Icon-LFG.png",
        headerSrc: "/images/projects/Header-LFG.png",
        platform: "Hypercasual · Desktop/Mobile · Solana",
        description:
          "Hyper casual endless vertical platformer with a ranking reward system powered by collectibles.",
        ctaLabel: "Play",
        href: "https://www.letsflamingo.gg/",
      },
      {
        id: "tuw",
        name: "The Underwatch",
        iconSrc: "/images/projects/Icon-TUW.png",
        headerSrc: "/images/projects/Header-TUW.png",
        platform: "Art · Ethereum",
        description:
          "Art project involving a collection of masks and metaverse gaming experiences.",
        ctaLabel: "Visit",
        href: "https://theunder.watch/",
        videoSrc: "https://www.youtube.com/watch?v=FVCsqBaLOj8",
        // SHA-256 of the access code
        requiresCode: true,
        codeHash: "61a3c22a310f6b6328941b54bab34d646e3ccd15a67504c7403ddef7d80f0e43",
      },
      {
        id: "folder-metaverse",
        name: "Metaverse",
        iconSrc: "/images/Icon-Projects.svg",
        projects: [
          {
            id: "ptg",
            name: "Path to Glory",
            iconSrc: "/images/projects/Icon-PTG.png",
            headerSrc: "/images/projects/Header-PTG.png",
            platform: "Fundraising",
            description:
              "Play to learn experience with tokenomics model for WeForest and Afya fundraising.",
            ctaLabel: "Play",
            href: "https://www.sandbox.game/en/experiences/path-to-glory!/2703f23c-2855-440f-b480-8ef60ffd1908/page",
            videoSrc: "https://www.youtube.com/watch?v=YrtYWkjAmX8",
            videoStartTime: 32,
          },
          {
            id: "dv",
            name: "Deja vu",
            iconSrc: "/images/projects/Icon-DV.png",
            headerSrc: "/images/projects/Header-DV.png",
            platform: "Narrative/Platformer",
            description:
              "Platformer with thriller narrative.",
            ctaLabel: "Play",
            videoSrc: "https://www.youtube.com/watch?v=gzP44XkLgpA",
            videoStartTime: 4,
          },
          {
            id: "dv2",
            name: "Deja vu 2",
            iconSrc: "/images/projects/Icon-DV2.png",
            headerSrc: "/images/projects/Header-DV2.png",
            platform: "Narrative/Platformer",
            description:
              "Sequel of the first episode.",
            ctaLabel: "Play",
            videoSrc: "https://www.youtube.com/watch?v=39sMaM1MHd0",
          },
          {
            id: "ttm",
            name: "Tiny Tanks",
            iconSrc: "/images/projects/Icon-TTM.png",
            headerSrc: "/images/projects/Header-TTM.png",
            platform: "Platformer · Arcade",
            description:
              "Arcade gameplay where players compete to be the last tank to fall.",
            ctaLabel: "Play",
            videoSrc: "https://www.youtube.com/watch?v=j4mUbYQaJgU",
            videoStartTime: 4920,
          },
          {
            id: "prison",
            name: "Prison Escape",
            iconSrc: "/images/projects/Icon-Prison.png",
            headerSrc: "/images/projects/Header-Prison.png",
            platform: "Platformers · Co-op",
            description:
              "Cooperative escape room where players interact to get out of the prison.",
            ctaLabel: "Play",
            videoSrc: "https://www.youtube.com/watch?v=PtB_w-LVhSw",
            videoStartTime: 28,
          },
          {
            id: "tycoon",
            name: "Donuts Tycoon",
            iconSrc: "/images/projects/Icon-Tycoon.png",
            headerSrc: "/images/projects/Header-Tycoon.png",
            platform: "Simulation · Tycoon",
            description:
              "Resource management game where the player evolves their donut delivery empire.",
            ctaLabel: "Play",
            videoSrc: "https://www.youtube.com/watch?v=BaWJUSax2GI",
            videoStartTime: 1390,
          },
          {
            id: "eatings",
            name: "Eatings Greetings",
            iconSrc: "/images/projects/Icon-Eatings.png",
            headerSrc: "/images/projects/Header-Eatings.png",
            platform: "Platformer · Seasonal",
            description:
              "Players eat all the holidays food with giant mouths to claim rewards of the season.",
            ctaLabel: "Play",
            videoSrc: "https://youtu.be/d9uALg_Zdlk",
            videoStartTime: 14,
          },
        ],
      },
    ],
  },
  //   {
  //   id: "AI",
  //   name: "AI",
  //   iconSrc: "/images/projects/Icon-Shooter.png",
  //   headerSrc: "/images/projects/Header-Shooter.png",
  //   platform: "WebGL · Desktop",
  //   description:
  //     "Psychological simulation prototype.",
  //   ctaLabel: "Coming Soon",
  //   href: "/projects/ia",
  //   // Example of a gated project (uncomment & replace with real hash if needed):
  //    requiresCode: true,
  //    codeHash: "c340e4845bc4f9cf9f28d6bcd2e31e689fa783f0bbe83e88b0572d1be42f61d8",
  // },
];
